import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { actionOnMessage, createMmWsAction } from '@skynet/data-ws-mm';
import { isOkCommand, parseResponse } from '@skynet/data-ws-core';
import type { SliceFactoryProps } from 'src/store/shared/types';
import type {
  AccountsThresholdState,
  DeleteAccountThresholdPayload,
  JsonAccountThreshold,
  JsonLoanAccountThresholdsSuccess,
} from './types';
import type { ProjectAccountThresholdCommands } from 'src/store/retainers/threshold/slice';
import type { LoanAccountThresholdCommands } from 'src/store/loans/threshold/slice';
import { resetStore } from 'src/store/actions';
import { listen as listenLoans } from 'src/store/loans/actions';
import { listen as listenRetainers } from 'src/store/retainers/actions';

function initThresholdStateFactory(opts?: Partial<AccountsThresholdState>) {
  const initialState: AccountsThresholdState = {
    thresholdByAccountId: {},
    requestStatus: null,
    updateStatus: null,
    updateCommand: null,
    error: null,
    updateData: null,
  };
  return { ...initialState, ...opts };
}

export function initThresholdSlice({
  prefix = 'shared',
  sliceName = 'threshold',
  initialState = initThresholdStateFactory(),
  extraReducers,
  commands,
}: SliceFactoryProps<
  AccountsThresholdState,
  typeof ProjectAccountThresholdCommands | typeof LoanAccountThresholdCommands
>) {
  if (!commands) {
    throw new Error('Commands are required for threshold slice factory');
  }

  const allowedCommands = commands ? Object.values(commands).map((cmd) => String(cmd)) : [];

  const setAccountThreshold = createMmWsAction<JsonAccountThreshold, JsonAccountThreshold>(
    `${prefix}/setAccountThresholdWs`,
    (data: JsonAccountThreshold) => ({
      command: commands.UpsertAccountThreshold,
      ...data,
    }),
  );

  const deleteAccountThreshold = createMmWsAction<
    DeleteAccountThresholdPayload,
    DeleteAccountThresholdPayload
  >(`${prefix}/deleteAccountThresholdWs`, (data) => ({
    command: commands.DeleteAccountThreshold,
    ...data,
  }));

  const slice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
      setThresholdUpdateData: (state, action: PayloadAction<JsonAccountThreshold>) => {
        state.updateData = action.payload;
      },
      /**
       * Clears the transient outcome of the last add/delete command.
       *
       * Consumers dispatch this once they have acted on that outcome (closed the modal, shown
       * the error). Without it `updateStatus` keeps its last value, so a remounted form reads a
       * stale `'success'` and reports the previous submit as if it had just happened.
       */
      resetThresholdUpdateStatus: (state) => {
        state.updateStatus = null;
        state.updateCommand = null;
        state.error = null;
        state.updateData = null;
      },
    },
    extraReducers: (builder) => {
      // listen
      if (typeof extraReducers === 'function') {
        extraReducers(builder);
      }

      builder.addCase(listenLoans, (state, action) => {
        const { section } = action.payload;
        const { isInitCall } = action.meta;
        if (section === 'terms' && isInitCall) {
          state.requestStatus = 'pending';
        }
      });

      builder.addCase(listenRetainers, (state, action) => {
        const { section } = action.payload;
        const { isInitCall } = action.meta;
        if (section === 'terms' && isInitCall) {
          state.requestStatus = 'pending';
        }
      });

      builder.addCase(actionOnMessage, (state, action) => {
        const { json, skipProcessing, error, errorCommand } = parseResponse(
          action.payload,
          allowedCommands,
        );

        if (skipProcessing || !json) return;

        console.debug(
          `${prefix}/${sliceName}/processing ${actionOnMessage.toString()}`,
          action,
          allowedCommands,
        );

        const { command, okCommand } = json;

        if (error) {
          state.error = error;
          switch (errorCommand) {
            case commands.UpsertAccountThreshold:
            case commands.DeleteAccountThreshold:
              state.updateStatus = 'error';
              state.updateCommand = errorCommand;
              break;
            case undefined:
            default:
          }
        }

        if (isOkCommand(command)) {
          switch (okCommand) {
            case commands.AccountThresholds:
              state.requestStatus = 'success';
              break;
            case commands.UpsertAccountThreshold: {
              if (state.updateData === null) {
                return;
              }
              const { accountID } = state.updateData;
              const existingThresholds = state.thresholdByAccountId?.[accountID] || [];
              state.thresholdByAccountId = {
                ...state.thresholdByAccountId,
                [accountID]: [...existingThresholds, state.updateData],
              };
              state.updateStatus = 'success';
              state.updateCommand = commands.UpsertAccountThreshold;
              state.updateData = null;
              break;
            }
            case commands.DeleteAccountThreshold: {
              if (state.updateData === null) {
                return;
              }
              const { accountID, token } = state.updateData;
              const updatedThresholds = state.thresholdByAccountId?.[accountID]?.filter(
                (threshold) => threshold.token !== token,
              );
              state.thresholdByAccountId = {
                ...state.thresholdByAccountId,
                [accountID]: updatedThresholds,
              };

              state.updateStatus = 'success';
              state.updateCommand = commands.DeleteAccountThreshold;
              state.updateData = null;
              break;
            }
            default:
          }
        }

        if (command === commands.AccountThresholds) {
          const { thresholds } = json as JsonLoanAccountThresholdsSuccess;
          state.thresholdByAccountId = thresholds.reduce<Record<string, JsonAccountThreshold[]>>(
            (acc, threshold) => {
              if (!acc[threshold.accountID]) {
                acc[threshold.accountID] = [];
              }
              acc[threshold.accountID].push(threshold);
              return acc;
            },
            {},
          );
          state.requestStatus = 'success';
        }
      });

      builder.addCase(setAccountThreshold, (state) => {
        state.updateStatus = 'pending';
        state.updateCommand = commands.UpsertAccountThreshold;
      });

      builder.addCase(resetStore, () => {
        return initialState;
      });
    },
  });

  const actions = {
    ...slice.actions,
    setAccountThreshold,
    deleteAccountThreshold,
  };

  return {
    actions,
    reducer: slice.reducer,
  };
}
